<!-- 轮播组件，替换van-swipe -->
<template>
  <div
    ref="swiperContainer"
    class="relative overflow-hidden w-full touch-pan-y select-none"
    @mouseenter="pauseAutoplay"
    @mouseleave="resumeAutoplay"
    @touchstart.passive="onTouchStart"
    @touchmove.prevent="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div
      ref="swiperWrapper"
      class="flex w-full swipe-wrapper will-change-transform"
      :class="
        isDragging
          ? 'transition-none'
          : 'transition-transform duration-500 ease-out'
      "
      :style="{
        transform: `translateX(-${currentIndex * 100 - dragOffsetPercent}% )`
      }"
    >
      <slot></slot>
    </div>

    <!-- 指示器（胶囊 + 圆点） -->
    <div
      v-if="showIndicators && itemCount > 1"
      class="absolute bottom-3 left-1/2 -translate-x-1/2 z-10"
    >
      <div
        class="flex items-center gap-1.5 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full"
      >
        <button
          v-for="(_, index) in itemCount"
          :key="index"
          class="transition-all duration-200"
          :class="[
            'h-1.5 rounded-full cursor-pointer',
            index === currentIndex ? 'w-4' : 'w-1.5'
          ]"
          :style="{
            backgroundColor:
              index === currentIndex
                ? props.indicatorColor
                : 'rgba(255,255,255,0.6)'
          }"
          :aria-current="index === currentIndex ? 'true' : 'false'"
          @click="goToSlide(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

interface Props {
  autoplay?: number
  indicatorColor?: string
  showIndicators?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: 0,
  indicatorColor: '#3b82f6',
  showIndicators: true
})

const currentIndex = ref(0)
const itemCount = ref(0)
const swiperWrapper = ref<HTMLElement>()
const swiperContainer = ref<HTMLElement>()
let autoplayTimer: NodeJS.Timeout | null = null
let isPaused = ref(false)

// 拖拽交互
const isDragging = ref(false)
const startX = ref(0)
const deltaX = ref(0)
const dragOffsetPercent = ref(0)

const updateItemCount = () => {
  nextTick(() => {
    if (swiperWrapper.value) {
      itemCount.value = swiperWrapper.value.children.length
    }
  })
}

const goToSlide = (index: number) => {
  currentIndex.value = index
}

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % itemCount.value
}

const startAutoplay = () => {
  if (props.autoplay > 0 && itemCount.value > 1) {
    autoplayTimer = setInterval(nextSlide, props.autoplay)
  }
}

const stopAutoplay = () => {
  if (autoplayTimer) {
    clearInterval(autoplayTimer)
    autoplayTimer = null
  }
}

const pauseAutoplay = () => {
  isPaused.value = true
  stopAutoplay()
}

const resumeAutoplay = () => {
  isPaused.value = false
  startAutoplay()
}

const onTouchStart = (e: TouchEvent) => {
  if (itemCount.value <= 1) return
  stopAutoplay()
  isDragging.value = true
  startX.value = e.touches[0].clientX
  deltaX.value = 0
  dragOffsetPercent.value = 0
}

const onTouchMove = (e: TouchEvent) => {
  if (!isDragging.value || !swiperContainer.value) return
  const current = e.touches[0].clientX
  deltaX.value = current - startX.value
  const width = swiperContainer.value.clientWidth || 1
  dragOffsetPercent.value = (deltaX.value / width) * 100
}

const onTouchEnd = () => {
  if (!isDragging.value || !swiperContainer.value) return
  const width = swiperContainer.value.clientWidth || 1
  const threshold = width * 0.2
  if (Math.abs(deltaX.value) > threshold) {
    if (deltaX.value > 0) {
      // 向右滑动 -> 上一个
      currentIndex.value =
        (currentIndex.value - 1 + itemCount.value) % itemCount.value
    } else {
      // 向左滑动 -> 下一个
      currentIndex.value = (currentIndex.value + 1) % itemCount.value
    }
  }
  isDragging.value = false
  dragOffsetPercent.value = 0
  if (!isPaused.value) resumeAutoplay()
}

onMounted(() => {
  updateItemCount()
  // 延迟启动自动轮播，确保组件完全挂载
  setTimeout(() => {
    if (props.autoplay > 0 && itemCount.value > 1) {
      startAutoplay()
    }
  }, 100)
})

onUnmounted(() => {
  stopAutoplay()
})

defineExpose({
  updateItemCount
})
</script>

<style scoped>
@keyframes progress {
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
}
</style>
